/**
 * useAdminMode — Zustand store for admin workspace-browsing mode.
 *
 * When an admin enters "admin mode" they can view another user's
 * workspaces / projects without being a member.  The backend handles
 * permission bypass via the JWT — the frontend only needs to:
 *   1. Know if admin mode is active
 *   2. Track which user's data is being viewed
 *   3. Provide enter / exit helpers
 *
 * Deep-linking:
 *   ?adminUserId=<id>   — AppShell reads this on mount to auto-enter admin mode
 */
import { create } from 'zustand'
import { safeLocalStorage } from '@/lib/safe-storage'

/**
 * Read the admin flag from localStorage user_data (same source hasFeature uses).
 */
function getIsAdmin() {
  try {
    const raw = safeLocalStorage.getItem('user_data')
    if (!raw) return false
    return JSON.parse(raw).isAdmin === true
  } catch {
    return false
  }
}

export const useAdminModeStore = create((set) => ({
  /** Whether the admin is currently viewing another user's data */
  isAdminMode: false,

  /** The userId whose workspaces are being viewed (null = own) */
  targetUserId: null,

  /** Optional display info for the target user (e.g. email) */
  targetUserLabel: '',

  /** Enter admin mode for a specific user */
  enterAdminMode: (userId, label = '') =>
    set({ isAdminMode: true, targetUserId: userId, targetUserLabel: label }),

  /** Exit admin mode and return to own data */
  exitAdminMode: () =>
    set({ isAdminMode: false, targetUserId: null, targetUserLabel: '' }),
}))

/**
 * Convenience hook — returns admin mode state + helpers.
 */
export function useAdminMode() {
  const isAdminMode = useAdminModeStore(s => s.isAdminMode)
  const targetUserId = useAdminModeStore(s => s.targetUserId)
  const targetUserLabel = useAdminModeStore(s => s.targetUserLabel)
  const enterAdminMode = useAdminModeStore(s => s.enterAdminMode)
  const exitAdminMode = useAdminModeStore(s => s.exitAdminMode)

  return {
    /** true when the logged-in user has isAdmin flag */
    isAdmin: getIsAdmin(),
    /** true when actively viewing another user's data */
    isAdminMode,
    targetUserId,
    targetUserLabel,
    enterAdminMode,
    exitAdminMode,
  }
}
